import { Button } from '@gerdau/hefesto/Button';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { Title } from '@gerdau/hefesto/Title';
import { Form, Input } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { ICustomer } from '../../store/ducks/customers/types';
import {
  cleanDashboard,
  loadBilledByDay,
  loadBilledByMonth,
  loadDuplicates,
  loadDuplicatesByDay,
  loadOpenOrderStatus,
  loadPaidByMonth
} from '../../store/ducks/dashboard/actions';
import { sendTagging } from '../../store/ducks/tagging/actions';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { uppercaseOnlyFirstLetter } from '../../utils/formatStrings';
import DashboardContent from './DashboardContent';
import OpenOrderStatusBySku from './OpenOrderStatusBySku';
import {
  CleanFilterButton,
  Container,
  EmptyContainer,
  FilterContainer,
  Header,
  Wrapper
} from './styles';

const { Search } = Input;

const Facelift: React.FC = () => {
  const dispatch = useDispatch();
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const dashboardState = useSelector(
    (state: ApplicationState) => state.dashboard
  );
  const userState = useSelector((state: ApplicationState) => state.user);

  const customerType: string =
    userState.searchCustomerType === ECustomerTypeView.EMITTER
      ? 'emissor'
      : 'recebedor';

  const [searchValue, setSearchValue] = useState('');

  const [customerId, setCustomerId] = useState(
    dashboardState.selectedCustomerId
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>();

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:PainelGestao',
        page: window.location.pathname,
        event: 'page_view'
      })
    );
  }, []);

  const loadDashboard = (customerId: string) => {
    const monthDate = moment();

    if (
      (userState.userPermissions.hasOrderViewAccess &&
        userState.roles.ordersViewCustomers.includes(customerId)) ||
      userState.isManager
    ) {
      dispatch(loadOpenOrderStatus(customerId, userState.searchCustomerType));
      dispatch(loadBilledByMonth(customerId, userState.searchCustomerType));
      dispatch(
        loadBilledByDay(customerId, userState.searchCustomerType, monthDate)
      );
    }

    if (
      (userState.userPermissions.hasFinancialAccess &&
        userState.roles.financialCustomers.includes(customerId)) ||
      userState.isManager
    ) {
      dispatch(loadPaidByMonth(customerId, userState.searchCustomerType));
      dispatch(loadDuplicates(customerId, userState.searchCustomerType));
      dispatch(
        loadDuplicatesByDay(customerId, userState.searchCustomerType, monthDate)
      );
    }
  };

  const onCustomerSelect = (selectedCustomerId: string) => {
    setView((prev) => ({
      ...prev,
      searchResultsView: true,
      hasSelectedEmitter: true
    }));

    dispatch(
      sendTagging({
        screenName: 'LG:PainelGestao',
        itemName: 'button:buscar',
        itemDescription: 'painelGestao_buscar',
        filterInfo: `#codEmr:${selectedCustomerId}`,
        event: 'click'
      })
    );

    if (userState.isManager && selectedCustomerId.length < 10) {
      selectedCustomerId = `0${selectedCustomerId}`;
    }

    if (selectedCustomerId !== customerId) {
      setCustomerId(selectedCustomerId);
    }

    dispatch(cleanDashboard());
    loadDashboard(selectedCustomerId);
  };

  const isLoading = (): boolean => {
    const {
      openOrderStatus,
      billedByMonth,
      paidByMonth,
      duplicates,
      billedByDay,
      duplicatesByDay
    } = dashboardState;
    return (
      (!!openOrderStatus && openOrderStatus.loading) ||
      (!!billedByMonth && billedByMonth.loading) ||
      (!!paidByMonth && paidByMonth.loading) ||
      (!!duplicates && duplicates.loading) ||
      (!!billedByDay && billedByDay.loading) ||
      (!!duplicatesByDay && duplicatesByDay.loading)
    );
  };

  const selectOptions: TSelectOptionProps[] = customersState.customers.reduce(
    (prev: TSelectOptionProps[], customer: ICustomer) =>
      userState.roles.financialCustomers.includes(customer.customerId) ||
      userState.roles.ordersViewCustomers.includes(customer.customerId)
        ? [
            ...prev,
            {
              value: customer.customerId,
              label: customer.name,
              descriptions: [
                `BP: ${customer.customerId}`,
                `CNPJ: ${customer.taxNumber}`,
                `${uppercaseOnlyFirstLetter(
                  customer.locality.address
                )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
                  customer.locality.state
                } | CEP ${customer.locality.zipCode}`
              ]
            }
          ]
        : prev,
    []
  );

  const [view, setView] = useState({
    searchView: true,
    skuView: false,
    searchResultsView: false,
    hasSelectedEmitter: false
  });

  return (
    <Container>
      {view.searchView && (
        <Header>
          <Title level={1}>Painel de Gestão</Title>
          <span className="subtitle">
            Selecione um {customerType} e acompanhe dados de volume e de
            pagamentos de pedidos.
          </span>
          {userState.isManager ? (
            <FilterContainer>
              <Form>
                <Form.Item>
                  <Search
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onSearch={() => onCustomerSelect(searchValue)}
                    disabled={isLoading()}
                    loading={isLoading()}
                    placeholder={'Buscar por código de cliente Gerdau'}
                  />
                </Form.Item>

                <CleanFilterButton
                  type="link"
                  onClick={() => {
                    setSearchValue('');
                    dispatch(cleanDashboard());
                  }}
                  disabled={isLoading()}
                >
                  Limpar
                </CleanFilterButton>
              </Form>
            </FilterContainer>
          ) : (
            <Wrapper>
              <Select
                data-testid="emittersSelect"
                disabled={isLoading() || customersState.loading}
                name="select"
                placeholder="- Selecione -"
                label={
                  customerType.charAt(0).toUpperCase() + customerType.slice(1)
                }
                options={selectOptions}
                onSelect={(id: string | string[]) =>
                  setSelectedCustomerId(id as string)
                }
                value={selectedCustomerId}
              />

              <Button
                loading={isLoading() || customersState.loading}
                onClick={() => onCustomerSelect(selectedCustomerId as string)}
                name="buscar"
                disabled={
                  isLoading() || customersState.loading || !selectedCustomerId
                }
              >
                Buscar
              </Button>
            </Wrapper>
          )}
        </Header>
      )}

      {view.searchResultsView && (
        <DashboardContent
          dashboardState={dashboardState}
          userPermissions={userState.userPermissions}
          viewOpenOrderStatusBySku={() =>
            setView({
              ...view,
              skuView: true,
              searchResultsView: false,
              searchView: false
            })
          }
          customerType={userState.searchCustomerType}
        />
      )}

      {!view.hasSelectedEmitter && (
        <EmptyContainer>
          <Title level={4}>
            Selecione um emissor para exibir os resultados.
          </Title>
        </EmptyContainer>
      )}

      {view.skuView && dashboardState.openOrderStatus?.dashboardBySku && (
        <OpenOrderStatusBySku
          backToDashboard={() =>
            setView({
              ...view,
              skuView: false,
              searchResultsView: true,
              searchView: true
            })
          }
          openOrderStatusState={dashboardState.openOrderStatus}
          selectedCustomerId={dashboardState.selectedCustomerId}
          customerType={userState.searchCustomerType}
        />
      )}
    </Container>
  );
};

export default Facelift;
